import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import {
  EnvelopeIcon, UserIcon, PhoneIcon,
  EyeIcon, EyeSlashIcon
} from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../stores';
import { Button, Input, Textarea, Select, CountrySelector, Card, CardBody } from '../../components/ui';
import { registrationQuestionService } from '../../services';

export default function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register: registerUser } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [phoneCountryCode, setPhoneCountryCode] = useState('EG');

  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const password = watch('password');

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const data = await registrationQuestionService.getQuestions(true);
      setQuestions(data);
    } catch (error) {
      console.error('Failed to fetch registration questions:', error);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const registrationData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        phone: data.phone || null,
        phoneCountryCode: data.phone ? phoneCountryCode : null,
        registrationAnswers: questions
          .filter(q => q.isActive)
          .map(q => ({
            questionId: q._id,
            answer: data[`question_${q._id}`]
          }))
          .filter(a => a.answer !== undefined && a.answer !== '')
      };

      const response = await registerUser(registrationData);

      if (response && response.requiresVerification) {
        toast.success(t('auth.registerSuccess'));
        navigate('/verify-email', { state: { email: data.email } });
      } else {
        toast.success(t('auth.registerSuccess'));
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('auth.register')} - ProCTM</title>
      </Helmet>

      <Card className="w-full max-w-md">
        <CardBody className="p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-neutral-dark">{t('auth.createAccount')}</h1>
            <p className="mt-2 text-gray-500">{t('auth.signUp')}</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t('auth.firstName')}
                icon={UserIcon}
                error={errors.firstName?.message}
                placeholder={t('auth.firstNamePlaceholder') || 'John'}
                {...register('firstName', {
                  required: t('errors.required'),
                  maxLength: { value: 50, message: t('errors.maxLength', { max: 50 }) }
                })}
              />

              <Input
                label={t('auth.lastName')}
                icon={UserIcon}
                error={errors.lastName?.message}
                placeholder={t('auth.lastNamePlaceholder') || 'Doe'}
                {...register('lastName', {
                  required: t('errors.required'),
                  maxLength: { value: 50, message: t('errors.maxLength', { max: 50 }) }
                })}
              />
            </div>

            <Input
              label={t('auth.email')}
              type="email"
              icon={EnvelopeIcon}
              error={errors.email?.message}
              placeholder={t('auth.emailPlaceholder') || 'you@example.com'}
              {...register('email', {
                required: t('errors.required'),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('errors.invalidEmail')
                }
              })}
            />

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-1">
                <CountrySelector
                  label={t('auth.country') || 'Country'}
                  value={phoneCountryCode}
                  onChange={setPhoneCountryCode}
                  error={errors.phoneCountryCode?.message}
                />
              </div>
              <div className="col-span-2">
                <Input
                  label={t('auth.phone') || 'Phone'}
                  type="tel"
                  icon={PhoneIcon}
                  error={errors.phone?.message}
                  placeholder={t('auth.phonePlaceholder') || '1234567890'}
                  {...register('phone')}
                />
              </div>
            </div>

            <div className="relative">
              <Input
                label={t('auth.password')}
                type={showPassword ? 'text' : 'password'}
                error={errors.password?.message}
                placeholder={t('auth.passwordPlaceholder') || '••••••••'}
                {...register('password', {
                  required: t('errors.required'),
                  minLength: { value: 8, message: t('errors.minLength', { min: 8 }) }
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-gray-400 hover:text-gray-600 transition-colors"
                tabIndex={-1}
                aria-label={showPassword ? t('auth.hidePassword') || 'Hide password' : t('auth.showPassword') || 'Show password'}
              >
                {showPassword ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>

            <div className="relative">
              <Input
                label={t('auth.confirmPassword')}
                type={showConfirmPassword ? 'text' : 'password'}
                error={errors.confirmPassword?.message}
                placeholder={t('auth.passwordPlaceholder') || '••••••••'}
                {...register('confirmPassword', {
                  required: t('errors.required'),
                  validate: value => value === password || t('auth.passwordMismatch')
                })}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-9 text-gray-400 hover:text-gray-600 transition-colors"
                tabIndex={-1}
                aria-label={showConfirmPassword ? t('auth.hidePassword') || 'Hide password' : t('auth.showPassword') || 'Show password'}
              >
                {showConfirmPassword ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>

            {questions.length > 0 && (
              <div className="space-y-4 pt-4 border-t border-gray-200">
                {questions.map((question) => (
                  <div key={question._id}>
                    {question.type === 'textarea' ? (
                      <Textarea
                        label={question.question}
                        rows={4}
                        error={errors[`question_${question._id}`]?.message}
                        placeholder={t('common.enterText') || 'Enter your answer...'}
                        {...register(`question_${question._id}`, {
                          required: question.required ? t('errors.required') : false
                        })}
                      />
                    ) : question.type === 'select' ? (
                      <Select
                        label={question.question}
                        options={[
                          { value: '', label: t('common.select') || 'Select...' },
                          ...question.options.map(opt => ({
                            value: opt.value,
                            label: opt.label
                          }))
                        ]}
                        error={errors[`question_${question._id}`]?.message}
                        {...register(`question_${question._id}`, {
                          required: question.required ? t('errors.required') : false
                        })}
                      />
                    ) : (
                      <Input
                        label={question.question}
                        type={question.type === 'number' ? 'number' : 'text'}
                        error={errors[`question_${question._id}`]?.message}
                        placeholder={t('common.enterText') || 'Enter your answer...'}
                        {...register(`question_${question._id}`, {
                          required: question.required ? t('errors.required') : false
                        })}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            <Button type="submit" className="w-full" loading={loading}>
              {t('auth.signUp')}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500">
            {t('auth.hasAccount')}{' '}
            <Link to="/login" className="text-primary font-medium hover:underline">
              {t('auth.signIn')}
            </Link>
          </p>
        </CardBody>
      </Card>
    </>
  );
}
